const pendingScripts = new Map();
const failedScripts = new WeakSet();
const timeoutMs = 15000;
let partyModule;

function ensureScript(name, url, isReady, attributes = {}) {
	if (isReady()) {
		return Promise.resolve();
	}
	if (!url) {
		return Promise.reject(new Error(`${name} script URL is missing. Please reload the page and try again.`));
	}

	const absoluteUrl = new URL(url, document.baseURI).href;
	if (pendingScripts.has(absoluteUrl)) {
		return pendingScripts.get(absoluteUrl);
	}

	let resolveReady;
	let rejectReady;
	const promise = new Promise((resolve, reject) => {
		resolveReady = resolve;
		rejectReady = reject;
	});
	pendingScripts.set(absoluteUrl, promise);

	let script = Array.from(document.scripts).find(candidate => candidate.src === absoluteUrl && !failedScripts.has(candidate));
	const created = !script;
	let finished = false;
	let poll;
	let timer;

	function cleanup() {
		clearInterval(poll);
		clearTimeout(timer);
		script?.removeEventListener('load', checkReady);
		script?.removeEventListener('error', failLoading);
		pendingScripts.delete(absoluteUrl);
	}

	function checkReady() {
		if (!finished && isReady()) {
			finished = true;
			cleanup();
			resolveReady();
		}
	}

	function fail(message) {
		if (finished) {
			return;
		}
		finished = true;
		cleanup();
		if (script) {
			failedScripts.add(script);
			if (created) {
				script.remove();
			}
		}
		rejectReady(new Error(message));
	}

	function failLoading() {
		fail(`${name} could not load. Please check your connection and try again.`);
	}

	try {
		if (created) {
			script = document.createElement('script');
			script.src = absoluteUrl;
			script.async = true;
			Object.assign(script, attributes);
		}
		script.addEventListener('load', checkReady);
		script.addEventListener('error', failLoading);
		// Polling also handles an existing eager script whose load event already fired.
		poll = setInterval(checkReady, 50);
		timer = setTimeout(() => fail(`${name} took too long to load. Please try again.`), timeoutMs);
		if (created) {
			document.head.appendChild(script);
		}
		checkReady();
	} catch {
		failLoading();
	}

	return promise;
}

export function ensureGoogleSdk() {
	return ensureScript('Google sign-in', 'https://accounts.google.com/gsi/client',
		() => typeof window.google?.accounts?.id?.initialize === 'function',
		{ referrerPolicy: 'strict-origin-when-cross-origin' });
}

export function ensureFacebookSdk() {
	return ensureScript('Facebook sign-in', 'https://connect.facebook.net/en_US/sdk.js',
		() => typeof window.FB?.init === 'function');
}

export function ensureStripeSdk() {
	return ensureScript('Card payments', 'https://js.stripe.com/v3/', () => typeof window.Stripe === 'function');
}

function existingScriptUrl(matches) {
	return Array.from(document.scripts).find(script => {
		if (!script.src) return false;
		try {
			return matches(new URL(script.src, document.baseURI));
		} catch {
			return false;
		}
	})?.src;
}

export function ensurePayPalSdk() {
	const url = window.bundleSdkUrls?.payPal || existingScriptUrl(url =>
		url.protocol === 'https:' && ['www.paypal.com', 'paypal.com'].includes(url.hostname) && url.pathname === '/sdk/js');
	return ensureScript('PayPal payments', url,
		() => typeof window.paypal?.Buttons === 'function');
}

export function ensurePaymentSdks() {
	return Promise.all([ensureStripeSdk(), ensurePayPalSdk()]);
}

export function ensurePartySdk() {
	if (typeof window.party?.confetti === 'function' && typeof window.party?.variation?.range === 'function') {
		return Promise.resolve();
	}
	// Vite emits a local, shared chunk. No celebration code is fetched on page load.
	return partyModule ??= import('party-js').then(module => {
		window.party = module.default;
	}).catch(error => {
		partyModule = null;
		throw error;
	});
}
